import { AudioQuality, Song, SubscriptionTier, AudioQualityUrls } from '../types/models';
import { 
  DEFAULT_AUDIO_QUALITY, 
  canAccessQuality, 
  getAvailableQualities,
  getMaxQualityForTier 
} from '../constants/audioQuality';

const AUDIO_QUALITY_STORAGE_KEY = 'naia_audio_quality';

export class AudioQualityService {
  /**
   * Get the user's preferred audio quality from localStorage
   */
  static getPreferredQuality(): AudioQuality {
    try {
      const stored = localStorage.getItem(AUDIO_QUALITY_STORAGE_KEY);
      if (stored && ['low', 'medium', 'high', 'max'].includes(stored)) {
        return stored as AudioQuality;
      }
    } catch (error) {
      console.warn('Failed to load audio quality preference:', error);
    }
    return DEFAULT_AUDIO_QUALITY;
  }

  /**
   * Set the user's preferred audio quality in localStorage
   */
  static setPreferredQuality(quality: AudioQuality): void {
    try {
      localStorage.setItem(AUDIO_QUALITY_STORAGE_KEY, quality);
    } catch (error) {
      console.warn('Failed to save audio quality preference:', error);
    }
  }

  /**
   * Get the best available audio quality for a song based on user's subscription and preferences
   */
  static getBestAvailableQuality(
    song: Song, 
    userTier: SubscriptionTier, 
    preferredQuality?: AudioQuality
  ): AudioQuality {
    const availableQualities = this.getSongAvailableQualities(song);
    const userAccessibleQualities = getAvailableQualities(userTier);
    const accessibleSongQualities = availableQualities.filter(q => 
      userAccessibleQualities.includes(q)
    );

    if (accessibleSongQualities.length === 0) {
      return 'low'; // Fallback to low quality
    }

    const preferred = preferredQuality || this.getPreferredQuality();
    
    // If preferred quality is available and accessible, use it
    if (accessibleSongQualities.includes(preferred)) {
      return preferred;
    }

    // Otherwise, use the highest available quality the user can access
    const qualityOrder: AudioQuality[] = ['max', 'high', 'medium', 'low'];
    for (const quality of qualityOrder) {
      if (accessibleSongQualities.includes(quality)) {
        return quality;
      }
    }

    return 'low'; // Final fallback
  }

  /**
   * Get available quality levels for a specific song
   */
  static getSongAvailableQualities(song: Song): AudioQuality[] {
    const qualities: AudioQuality[] = [];
    
    // Check legacy audioUrl first (fallback to low quality)
    if (song.audioUrl) {
      qualities.push('low');
    }

    // Check quality-specific URLs
    if (song.audioUrls) {
      if (song.audioUrls.low) qualities.push('low');
      if (song.audioUrls.medium) qualities.push('medium');
      if (song.audioUrls.high) qualities.push('high');
      if (song.audioUrls.max) qualities.push('max');
    }

    return qualities.length > 0 ? qualities : ['low'];
  }

  /**
   * Get the audio URL for a specific quality level
   */
  static getAudioUrlForQuality(song: Song, quality: AudioQuality): string | null {
    // Check quality-specific URLs first
    if (song.audioUrls) {
      const qualityUrl = song.audioUrls[quality];
      if (qualityUrl) return qualityUrl;
    }

    // Fallback to legacy audioUrl for any quality if no specific URL exists
    if (song.audioUrl && quality === 'low') {
      return song.audioUrl;
    }

    // Try to fallback to next available quality
    const availableQualities = this.getSongAvailableQualities(song);
    if (availableQualities.length === 0) return null;

    // Find the closest available quality
    const qualityOrder: AudioQuality[] = ['max', 'high', 'medium', 'low'];
    const targetIndex = qualityOrder.indexOf(quality);
    
    // Try lower qualities first (better than nothing)
    for (let i = targetIndex + 1; i < qualityOrder.length; i++) {
      const fallbackQuality = qualityOrder[i];
      if (availableQualities.includes(fallbackQuality)) {
        if (song.audioUrls?.[fallbackQuality]) {
          return song.audioUrls[fallbackQuality];
        }
        if (fallbackQuality === 'low' && song.audioUrl) {
          return song.audioUrl;
        }
      }
    }

    // Try higher qualities as last resort
    for (let i = 0; i < targetIndex; i++) {
      const fallbackQuality = qualityOrder[i];
      if (availableQualities.includes(fallbackQuality) && song.audioUrls?.[fallbackQuality]) {
        return song.audioUrls[fallbackQuality];
      }
    }

    return song.audioUrl || null; // Final fallback
  }

  /**
   * Validate if user can access a specific quality level
   */
  static canUserAccessQuality(userTier: SubscriptionTier, quality: AudioQuality): boolean {
    return canAccessQuality(userTier, quality);
  }

  /**
   * Get the maximum quality a user can access
   */
  static getUserMaxQuality(userTier: SubscriptionTier): AudioQuality {
    return getMaxQualityForTier(userTier);
  }

  /**
   * Create AudioQualityUrls object from individual URL parameters
   */
  static createAudioQualityUrls(
    low?: string,
    medium?: string,
    high?: string,
    max?: string
  ): AudioQualityUrls {
    const urls: AudioQualityUrls = {};
    if (low) urls.low = low;
    if (medium) urls.medium = medium;
    if (high) urls.high = high;
    if (max) urls.max = max;
    return urls;
  }

  /**
   * Migrate legacy song object to include quality URLs
   */
  static migrateLegacySong(song: Song): Song {
    if (!song.audioUrls && song.audioUrl) {
      return {
        ...song,
        audioUrls: {
          low: song.audioUrl
        }
      };
    }
    return song;
  }
}